import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";
import {
  Wrench, PackageSearch, ClipboardCheck, Sparkles, CheckCircle2, Clock,
  AlertCircle, Flame, Loader2, Maximize2, Minimize2, RefreshCw,
} from "lucide-react";

/**
 * Modo Oficina (TV board) — full-screen operational view optimised for
 * 55"+ displays in the workshop. Updates every 30s. Kanban columns map
 * the SOM lifecycle to the 4 physical stages the mechanic / supervisor
 * actually cares about:
 *   • Diagnóstico      → Em diagnóstico, Aguarda classificação, Orçamento, Aguarda aprovação
 *   • Em reparação     → Em reparação, Entrada marcada, Peças disponíveis
 *   • Controlo         → Controlo de qualidade, Em lavagem
 *   • Pronto           → Pronto para entrega
 *
 * KPIs footer: SOMs abertas hoje, SOMs fechadas hoje, carga total,
 * casos com SLA crítica (>72h sem actualização).
 *
 * Branch scoping: HQ users see all open SOMs across branches; dealer users
 * see only their own branch (enforced by backend RBAC).
 */

const COLUMNS = [
  {
    key: "diag",
    title: "Diagnóstico & Orçamento",
    Icon: PackageSearch,
    accent: "border-t-sky-400 bg-sky-50/40",
    chip: "bg-sky-100 text-sky-800",
    statuses: new Set([
      "Em diagnóstico", "Aguarda classificação",
      "Orçamento em preparação", "Orçamento pronto",
      "Aguarda envio ao cliente", "Aguarda aprovação do cliente",
      "Aprovado",
    ]),
  },
  {
    key: "repair",
    title: "Em reparação",
    Icon: Wrench,
    accent: "border-t-amber-400 bg-amber-50/40",
    chip: "bg-amber-100 text-amber-800",
    statuses: new Set([
      "Aguarda pagamento ou PO", "Aguarda pedido de peças", "Aguarda peças",
      "Peças parcialmente recebidas", "Peças disponíveis",
      "Entrada marcada", "Em reparação",
    ]),
  },
  {
    key: "qc",
    title: "Controlo & Lavagem",
    Icon: ClipboardCheck,
    accent: "border-t-indigo-400 bg-indigo-50/40",
    chip: "bg-indigo-100 text-indigo-800",
    statuses: new Set(["Controlo de qualidade", "Em lavagem"]),
  },
  {
    key: "ready",
    title: "Pronto para entrega",
    Icon: Sparkles,
    accent: "border-t-emerald-400 bg-emerald-50/40",
    chip: "bg-emerald-100 text-emerald-800",
    statuses: new Set(["Pronto para entrega"]),
  },
];

const CLOSED = new Set(["Entregue", "Chamada de cortesia", "Encerrado", "Cancelado"]);

function hoursSince(iso) {
  if (!iso) return 0;
  return (Date.now() - new Date(iso).getTime()) / 36e5;
}

function fmtHours(h) {
  if (h < 1) return `${Math.round(h * 60)}min`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${Math.floor(h / 24)}d ${Math.round(h % 24)}h`;
}

export default function WorkshopBoardPage() {
  const { user } = useAuth();
  const [cases, setCases] = useState(null);
  const [tick, setTick] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    try {
      // Pull enough for a wide workshop; most dealers see <100 open SOMs.
      // Backend RBAC + current branch_scope auto-filter apply.
      const r = await api.get("/service-cases", { params: { limit: 500 } });
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setCases(list.filter((c) => !CLOSED.has(c.current_status)));
      setLastRefresh(new Date());
    } catch {
      // keep previous snapshot visible on transient failure
      setLastRefresh(new Date());
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => { if (tick > 0) load(); }, [tick, load]);

  // Fullscreen handling
  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const columns = useMemo(() => {
    if (!cases) return COLUMNS.map((c) => ({ ...c, items: [] }));
    return COLUMNS.map((col) => ({
      ...col,
      items: cases
        .filter((c) => col.statuses.has(c.current_status))
        .sort((a, b) => hoursSince(b.updated_at || b.opened_at) - hoursSince(a.updated_at || a.opened_at)),
    }));
  }, [cases]);

  const kpis = useMemo(() => {
    if (!cases) return { open: 0, criticalSla: 0, oldest: 0, byPrio: {} };
    const now = new Date();
    const todayISO = now.toISOString().slice(0, 10);
    let openedToday = 0, criticalSla = 0, oldest = 0;
    const byPrio = { "Baixa": 0, "Normal": 0, "Alta": 0, "Urgente": 0 };
    cases.forEach((c) => {
      if ((c.opened_at || "").startsWith(todayISO)) openedToday += 1;
      const h = hoursSince(c.updated_at || c.opened_at);
      if (h > oldest) oldest = h;
      if (h > 72) criticalSla += 1;
      const p = c.priority || "Normal";
      byPrio[p] = (byPrio[p] || 0) + 1;
    });
    return { open: cases.length, openedToday, criticalSla, oldest, byPrio };
  }, [cases]);

  // Hide sidebar via class trick — the Layout respects `rcc-fullscreen` on body
  useEffect(() => {
    document.body.classList.add("workshop-board-active");
    return () => document.body.classList.remove("workshop-board-active");
  }, []);

  if (cases === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400 bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> A carregar quadro operacional…
      </div>
    );
  }

  const branchHint = user?.all_branches ? "Todas as filiais" : (user?.branch || "");

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col" data-testid="workshop-board">
      {/* Top banner */}
      <header className="bg-ronil-navy text-white px-6 py-3 flex items-center gap-4 shrink-0">
        <div className="w-9 h-9 bg-white/10 rounded-md flex items-center justify-center">
          <Wrench className="w-5 h-5 text-ronil-gold" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-ronil-gold font-heading font-bold">Modo Oficina</div>
          <h1 className="font-display text-xl font-bold leading-none mt-0.5">
            Quadro Operacional {branchHint && <span className="text-ronil-gold-muted font-heading font-normal text-sm">· {branchHint}</span>}
          </h1>
        </div>
        <div className="ml-auto flex items-center gap-3 text-sm">
          <span className="inline-flex items-center gap-1.5 text-slate-200" data-testid="board-last-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
            {lastRefresh ? lastRefresh.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}
          </span>
          <button
            onClick={toggleFullscreen}
            className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 text-white text-xs px-3 py-1.5 rounded-md transition"
            data-testid="board-fullscreen-btn"
          >
            {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            {isFullscreen ? "Sair" : "Ecrã inteiro"}
          </button>
        </div>
      </header>

      {/* Kanban */}
      <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 p-4">
        {columns.map((col) => {
          const Icon = col.Icon;
          return (
            <section key={col.key} className={`flex flex-col rounded-lg border border-slate-200 border-t-4 ${col.accent} overflow-hidden`} data-testid={`board-col-${col.key}`}>
              <header className="px-4 py-3 flex items-center gap-2 bg-white/70 backdrop-blur border-b border-slate-200">
                <Icon className="w-4 h-4 text-slate-700" />
                <h2 className="font-heading font-bold text-slate-800 text-sm">{col.title}</h2>
                <span className={`ml-auto text-xs font-heading font-bold px-2 py-0.5 rounded-md ${col.chip}`}>{col.items.length}</span>
              </header>
              <ul className="flex-1 overflow-y-auto p-2 space-y-2">
                {col.items.length === 0 && (
                  <li className="text-xs text-slate-400 text-center py-6">Sem casos nesta fase.</li>
                )}
                {col.items.map((c) => {
                  const h = hoursSince(c.updated_at || c.opened_at);
                  const isHot = h > 72;
                  const isWarm = !isHot && h > 24;
                  return (
                    <li
                      key={c.id}
                      className={`bg-white rounded-md p-3 shadow-sm border ${isHot ? "border-rose-300" : isWarm ? "border-amber-300" : "border-slate-200"}`}
                      data-testid={`board-card-${c.number}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-heading font-bold text-ronil-navy text-sm leading-tight truncate">{c.number}</div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {c.plate || c.vehicle_plate || "—"} · {c.vehicle_brand || c.brand || ""} {c.vehicle_model || ""}
                          </div>
                        </div>
                        {c.priority && (
                          <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                            c.priority === "Urgente" ? "bg-rose-100 text-rose-700" :
                            c.priority === "Alta" ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>{c.priority}</span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center justify-between text-[10px]">
                        <span className="text-slate-500 truncate">{c.branch}</span>
                        <span className={`inline-flex items-center gap-1 font-mono ${isHot ? "text-rose-600 font-bold" : isWarm ? "text-amber-600" : "text-slate-400"}`}>
                          <Clock className="w-3 h-3" /> {fmtHours(h)}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>

      {/* KPI footer */}
      <footer className="bg-white border-t border-slate-200 px-6 py-3 shrink-0 grid grid-cols-2 sm:grid-cols-5 gap-4" data-testid="board-kpis">
        <KPI label="Abertas hoje" value={kpis.openedToday} Icon={Sparkles} tone="emerald" />
        <KPI label="Em curso" value={kpis.open} Icon={Wrench} tone="navy" />
        <KPI label="SLA crítica (>72h)" value={kpis.criticalSla} Icon={Flame} tone={kpis.criticalSla > 0 ? "rose" : "slate"} />
        <KPI label="Caso mais antigo" value={fmtHours(kpis.oldest)} Icon={AlertCircle} tone="amber" />
        <KPI label="Urgentes" value={kpis.byPrio["Urgente"] || 0} Icon={CheckCircle2} tone={kpis.byPrio["Urgente"] ? "rose" : "slate"} />
      </footer>
    </div>
  );
}

const TONES = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  navy: "bg-ronil-navy-50 text-ronil-navy border-slate-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  slate: "bg-slate-50 text-slate-600 border-slate-200",
};

function KPI({ label, value, Icon, tone = "navy" }) {
  return (
    <div className={`rounded-md border px-3 py-2 flex items-center gap-3 ${TONES[tone] || TONES.navy}`}>
      <Icon className="w-5 h-5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-widest font-heading font-semibold opacity-80">{label}</div>
        <div className="font-heading font-bold text-xl leading-none mt-0.5 tabular-nums">{value}</div>
      </div>
    </div>
  );
}
